<template>
  <div class="twcs-fixed twcs-z-[1000] twcs-inset-0 twcs-overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" @keydown="onKeydownEvent">
    <div class="twcs-block twcs-items-end twcs-justify-center twcs-min-h-screen twcs-p-1 sm:twcs-pt-4 sm:twcs-px-4 sm:twcs-pb-20 twcs-text-center sm:twcs-block">
      <Transition
        enter-active-class="twcs-ease-in-out twcs-duration-300"
        enter-from-class="twcs-opacity-0"
        enter-to-class="twcs-opacity-100"
        leave-active-class="twcs-ease-in-out twcs-duration-200"
        leave-from-class="twcs-opacity-100"
        leave-to-class="twcs-opacity-0"
      >
        <!--
          Background overlay, show/hide based on modal state.

          Entering: "ease-out duration-300"
            From: "opacity-0"
            To: "opacity-100"
          Leaving: "ease-in duration-200"
            From: "opacity-100"
            To: "opacity-0"
        -->
        <div v-if="modalShow" class="twcs-absolute twcs-inset-0 twcs-bg-gray-500 twcs-bg-opacity-75 twcs-transition-opacity" aria-hidden="true"></div>
      </Transition>
      <Transition
        enter-active-class="twcs-ease-out twcs-duration-300"
        enter-from-class="twcs-opacity-0 twcs-translate-y-4 sm:twcs-translate-y-0 sm:twcs-scale-95"
        enter-to-class="twcs-opacity-100 twcs-translate-y-0 sm:twcs-scale-100"
        leave-active-class="twcs-ease-in twcs-duration-200"
        leave-from-class="twcs-opacity-100 twcs-translate-y-0 sm:twcs-scale-100"
        leave-to-class="twcs-opacity-0 twcs-translate-y-4 sm:twcs-translate-y-0 sm:twcs-scale-95"
      >
        <!--
          Modal panel, show/hide based on modal state.

          Entering: "ease-out duration-300"
            From: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            To: "opacity-100 translate-y-0 sm:scale-100"
          Leaving: "ease-in duration-200"
            From: "opacity-100 translate-y-0 sm:scale-100"
            To: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        -->
        <div v-if="modalShow" class="twcs-inline-block twcs-align-bottom twcs-bg-white twcs-rounded-lg twcs-px-4 twcs-pt-5 twcs-pb-4 twcs-text-left twcs-overflow-y-auto twcs-shadow-xl twcs-transform twcs-transition-all sm:twcs-my-8 sm:twcs-align-top twcs-w-full sm:twcs-p-6">
          <div class="twcs-relative twcs-mb-4">
            <div class="twcs-px-4 twcs-sticky">
              <div class="twcs-grid twcs-grid-cols-6 twcs-gap-2">
                <div class="twcs-col-span-1"></div>
                <div class="twcs-col-span-4 md:twcs-col-span-5 twcs-col-start-2 md:twcs-col-start-1 twcs-justify-center">
                  <h2 id="modal-title" class="twcs-m-0 twcs-text-center md:twcs-text-left twcs-text-lg twcs-tracking-normal twcs-font-semibold">
                    {{ classModel.subject }} {{ classModel.catalog_number }}: {{ classModel.title }}
                  </h2>
                </div>
                <div class="twcs-col-span-1 twcs-flex twcs-items-center twcs-justify-end">
                  <button
                    @click="onClose()"
                    class="twcs-bg-white twcs-rounded-md twcs-text-gray-400 hover:twcs-text-gray-500 focus:twcs-outline-none focus-visible:twcs-ring-2 focus-visible:twcs-ring-offset-2 focus-visible:twcs-ring-kctcs-gold-200"
                    ref="closeButtonRef"
                  >
                    <span class="twcs-sr-only">Close panel</span>
                    <!-- Heroicon name: outline/x -->
                    <svg class="twcs-h-6 twcs-w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div ref="classInfoRef">
              <div v-if="filterSearchStore.showCollegeFilter" class="twcs-text-base twcs-px-4 twcs-pb-4">
                {{ collegeKey[classModel.college] }}
              </div>
              <div class="twcs-min-w-0 twcs-flex-1 twcs-px-4 md:twcs-grid md:twcs-grid-cols-2 md:twcs-gap-4">
                <div class="twcs-mt-4 md:twcs-mt-0">
                  <p class="twcs-text-base twcs-text-gray-700">
                    Format: {{ formatKey[classModel.format] }}
                  </p>
                  <p class="twcs-text-base twcs-text-gray-700">
                    <template v-if="classModel.room !== null">
                      <template v-if="classModel.building_description !== null && classModel.building_description.indexOf(classModel.room) !== -1">
                        <!-- building_description has room in it -->
                        Location: {{ classModel.building_description }}
                      </template>
                      <template v-else>
                        Location: {{ classModel.building_description }}, Room: {{ classModel.room}}
                      </template>
                    </template>
                    <template v-else-if="classModel.building === 'WWW' || classModel.building === 'WWL'">
                      Location: Online
                    </template>
                    <template v-else>
                      Location: {{ classModel.building }}
                    </template>
                  </p>
                  <p class="twcs-text-base twcs-text-gray-700">
                    Campus: {{ classModel.location }}
                  </p>
                  <p class="twcs-text-base twcs-text-gray-700">
                    Class: {{ classModel.number }}
                  </p>
                  <p class="twcs-text-base twcs-text-gray-700">
                    Section: {{ classModel.section }}
                  </p>
                  <p class="twcs-text-base twcs-text-gray-700">
                    Credit Hours (max): {{ classModel.hours }}
                  </p>
                </div>
                <div class="twcs-mt-2 md:twcs-mt-0">
                  <p class="twcs-text-base twcs-text-gray-700">
                    Date:
                    <time :datetime="classModel.starts_on">{{ dateFormatedToString(classModel.starts_on) }}</time>
                    - <time :datetime="classModel.ends_on">{{ dateFormatedToString(classModel.ends_on) }}</time>
                  </p>
                  <p class="twcs-text-base twcs-text-gray-700">
                    Days of the Week: {{ classModel.meeting_pattern }}
                  </p>
                  <p class="twcs-text-base twcs-text-gray-700">
                    <template v-if="classModel.begin_time === 'TBA'">
                      Time: {{ classModel.begin_time }}
                    </template>
                    <template v-else>
                      Time: <time :datetime="convertTo24Hr(classModel.begin_time)">{{classModel.begin_time}}</time> - <time :datetime="convertTo24Hr(classModel.end_time)">{{classModel.end_time}}</time>
                    </template>
                  </p>
                  <p class="twcs-text-base twcs-text-gray-700">
                    Instructor: {{ classModel.instructor.replace(',', ', ') }}
                  </p>
                  <p class="twcs-text-base twcs-text-gray-700">
                    Instruction Mode: {{ instructionModeKey[classModel.instruction_mode] }}
                  </p>
                  <p class="twcs-text-base twcs-text-gray-700">
                    Enrolled: {{ classModel.enrolled }} of {{ classModel.max_enrollment }}
                  </p>
                </div>
                <div class="twcs-mt-2 md:twcs-mt-0 md:twcs-col-span-2">
                  <p class="twcs-text-base twcs-text-gray-700">
                    Notes: {{ classModel.notes }}
                  </p>
									<p v-if="classModel.notes_2 !== '-'" class="twcs-text-base twcs-text-gray-700">
                     {{ classModel.notes_2 }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { dateFormated, convertTo24HrTime } from '@/helpers/Date.js';
import { useFilterSearchStore } from '@/stores/filter-search.js';
import { formatKey, collegeKey, instructionModeKey } from '@/constants/lookup-keys.js';

defineProps({
	modalOpen: {
		type: Boolean,
		required: true,
	},
	modalShow: {
		type: Boolean,
		required: true,
	},
});

let emit = defineEmits(['modalCloseOnClick']);

let filterSearchStore = useFilterSearchStore();
let classModel = computed(() => filterSearchStore.searchData.classModel);

let buttonRef = document.activeElement; // ref to the button that opens modal
let classInfoRef = ref(null);
let closeButtonRef = ref(null);
let currentFocusedRef = ref(0);
let focusables = [
	closeButtonRef,
	classInfoRef,
];

onMounted(() => {
	setTimeout(() => {
		closeButtonRef.value.focus();
	}, 300);
});

function onClose() {
	buttonRef.focus();
	emit('modalCloseOnClick', false);
}

function dateFormatedToString(date) {
	return dateFormated(date);
}

function convertTo24Hr(time) {
	return convertTo24HrTime(time);
}

function onKeydownEvent(event) {
	if(event.key === 'ArrowDown' || event.key === 'ArrowUp') {
		event.preventDefault();
		return;
	}

	if(event.key === 'Escape') {
		onClose();
		event.preventDefault();
		return;
	}

	if(event.key !== 'Tab') {
		return;
	}
	if(event.shiftKey) {
		// shift+tab
		currentFocusedRef.value = currentFocusedRef.value === 0 ? focusables.length - 1 : currentFocusedRef.value - 1;
		focusables[currentFocusedRef.value].value.focus();
		event.preventDefault();
	} else {
		// just tab
		currentFocusedRef.value = currentFocusedRef.value === focusables.length - 1 ? 0 : currentFocusedRef.value + 1;
		focusables[currentFocusedRef.value].value.focus();
		event.preventDefault();
	}
}
</script>
